/**
 * Cloudflare R2 media upload / download helper.
 *
 * Works in tandem with the backend endpoints declared in /app/backend/server.py:
 *   POST   /api/media/presign-upload      → { key, url, content_type, ... }
 *   POST   /api/media/finalize            → persists gallery_items record
 *   GET    /api/media/download-url/{id}?download=bool  → signed URL
 *   DELETE /api/media/{id}                → removes from R2 + DB
 *   PATCH  /api/media/{id}/move           → move between folders
 *
 * The browser uploads DIRECTLY to R2 (bypasses our FastAPI backend), so a
 * 50 MB video never touches our server RAM. A dedicated progress callback
 * is surfaced so callers can render a progress bar.
 */
import api from "./api";
import { BACKEND_URL } from "../lib/api";
import { brandSlug } from "./brand";

/** Detect media category from a File or URL filename. */
export function kindOf(file) {
  const mt = (file?.type || "").toLowerCase();
  if (mt.startsWith("image/")) return "photo";
  if (mt.startsWith("video/")) return "video";
  const name = (file?.name || "").toLowerCase();
  if (/\.(jpe?g|png|webp|heic|heif|gif)$/.test(name)) return "photo";
  if (/\.(mp4|mov|webm|m4v)$/.test(name)) return "video";
  return "file";
}

async function mediaStatusIsConfigured() {
  try {
    const { data } = await api.get("/media/status");
    return !!data?.r2_configured;
  } catch {
    return false;
  }
}

/**
 * Upload a single File directly to R2 and create the gallery_items record.
 *
 * @param {File}   file
 * @param {Object} opts
 * @param {string} opts.customerId    required
 * @param {string=} opts.folderId
 * @param {string=} opts.parentType   "jobcards" | "tickets" | "tasks" | "leads"
 * @param {string=} opts.parentId
 * @param {Function=} opts.onProgress percent (0-100) callback
 * @returns {Promise<Object>} the persisted gallery_items record
 */
export async function uploadMedia(file, opts = {}) {
  const {
    customerId,
    folderId,
    parentType,
    parentId,
    onProgress,
  } = opts;
  if (!customerId) throw new Error("customerId required for uploadMedia");

  // 1. Presign
  const presign = await api.post("/media/presign-upload", {
    customer_id: customerId,
    filename: file.name || "upload",
    content_type: file.type || "application/octet-stream",
    size: file.size,
    parent_type: parentType,
    parent_id: parentId,
    folder_id: folderId,
  });
  const { key, url, content_type } = presign.data;

  // 2. Browser PUT straight to R2 with progress reporting. We use XHR so we
  //    can get upload progress events (fetch's native progress API is still
  //    patchy across browsers).
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    xhr.setRequestHeader("Content-Type", content_type);
    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`R2 upload failed (HTTP ${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("Network error during R2 upload"));
    xhr.send(file);
  });

  // 3. Finalize — persist the DB record.
  const fin = await api.post("/media/finalize", {
    customer_id: customerId,
    key,
    filename: file.name || "upload",
    content_type,
    size: file.size,
    kind: kindOf(file),
    parent_type: parentType,
    parent_id: parentId,
    folder_id: folderId,
  });
  return fin.data;
}

/** Batch-upload helper — returns { succeeded, failed } arrays. */
export async function uploadManyMedia(files, opts = {}, perFileProgress) {
  // Bulk upload with up to 3 concurrent transfers — much faster on phones
  // with multiple photos. Caller's progress callback receives (idx, pct, done, total).
  const succeeded = [];
  const failed = [];
  const total = files.length;
  let done = 0;
  const CONCURRENCY = Math.min(3, Math.max(1, total));
  let cursor = 0;
  const worker = async () => {
    while (true) {
      const i = cursor++;
      if (i >= total) return;
      const f = files[i];
      try {
        const item = await uploadMedia(f, {
          ...opts,
          onProgress: (p) => perFileProgress?.(i, p, done, total),
        });
        succeeded.push(item);
      } catch (e) {
        failed.push({ file: f, error: e?.message || String(e) });
      } finally {
        done += 1;
        perFileProgress?.(i, 100, done, total);
      }
    }
  };
  await Promise.all(Array.from({ length: CONCURRENCY }, worker));
  return { succeeded, failed };
}

/**
 * Get a short-lived signed URL to view or download a media item.
 * Use `{ download: true }` to force the browser to save it to disk with its
 * original filename instead of rendering it inline.
 */
export async function signedMediaUrl(itemId, { download = false, thumb = false } = {}) {
  const qs = new URLSearchParams();
  if (download) qs.set("download", "true");
  if (thumb) qs.set("thumb", "true");
  const { data } = await api.get(
    `/media/download-url/${itemId}${qs.toString() ? `?${qs}` : ""}`,
  );
  return data; // { url, expires_in, filename, kind, size, content_type, has_thumb, is_thumb }
}

/**
 * Trigger a device-side download (Save to camera roll / Downloads folder).
 *
 * We deliberately AVOID `fetch().blob()` against R2 here — it triggers a
 * CORS preflight that the bucket doesn't permit and breaks downloads in
 * production. The presigned URL already carries
 * `Content-Disposition: attachment; filename=...`, so a plain anchor click
 * is enough to make the browser save the file. Top-level navigation isn't
 * subject to the R2 bucket's CORS policy.
 */
export async function downloadMediaToDevice(item) {
  const info = await signedMediaUrl(item.id, { download: true });
  const a = document.createElement("a");
  a.href = info.url;
  // `download` is ignored cross-origin but harmless. Content-Disposition
  // from R2 is what actually forces the save behaviour.
  a.download = info.filename || item.filename || "download";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export async function deleteMedia(itemId) {
  await api.delete(`/media/${itemId}`);
}

export async function moveMedia(itemId, folderId) {
  await api.patch(`/media/${itemId}/move`, { folder_id: folderId || null });
}

/** Move multiple media items into one folder in a single call. */
export async function bulkMoveMedia(ids, folderId) {
  const { data } = await api.post("/media/bulk-move", {
    ids,
    folder_id: folderId || null,
  });
  return data; // { moved, errors }
}

/**
 * Zip multiple media items on the client and trigger a single download.
 * We stream signed URL fetches into JSZip. For a 50-item folder this uses
 * ~50× a single file's bandwidth (which is fine because R2 egress is free).
 */
export async function downloadMediaAsZip(items, zipName, onProgress) {
  if (!items?.length) return;
  zipName = zipName || `${brandSlug()}-download.zip`;
  // Lazy-load JSZip so the main bundle stays slim.
  const { default: JSZip } = await import("jszip");
  const zip = new JSZip();

  let done = 0;
  for (const it of items) {
    try {
      let url;
      let fname;
      let useAuth = false;
      if (it.r2_key) {
        // Iter 290zp.13z12 — use same-origin streaming proxy to dodge
        // R2's missing CORS headers. The browser fetches bytes from
        // FastAPI (same origin as the app) instead of R2 directly.
        url = `${BACKEND_URL}/api/media/download-stream/${encodeURIComponent(it.id)}?download=true`;
        fname = it.filename || `file-${it.id}`;
        useAuth = true;
      } else if (it.file_url) {
        // Legacy upload (pre-R2). [Iter 327] Route via uploadUrl() so
        // the token is fresh and the URL is normalised.
        const { uploadUrl } = await import("./uploads");
        url = uploadUrl(it.file_url);
        fname = it.file_name || `file-${it.id}`;
      } else {
        continue;
      }
      const fetchOpts = useAuth
        ? { headers: { Authorization: `Bearer ${localStorage.getItem("hv_token") || ""}` } }
        : {};
      const resp = await fetch(url, fetchOpts);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      // Handle duplicate filenames in a folder — JSZip overwrites silently.
      let uniq = fname;
      let n = 1;
      while (zip.file(uniq)) {
        const dot = fname.lastIndexOf(".");
        if (dot > 0) uniq = `${fname.slice(0, dot)} (${n})${fname.slice(dot)}`;
        else uniq = `${fname} (${n})`;
        n++;
      }
      zip.file(uniq, blob);
      done++;
      onProgress?.(done, items.length);
    } catch (e) {
      // Skip failed files but keep going — better to get partial zip than nothing.
      console.warn("zip skipped", it.id, e);
    }
  }

  const blob = await zip.generateAsync({ type: "blob" }, (m) => {
    if (m.percent != null) onProgress?.(done, items.length, m.percent);
  });
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = zipName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objUrl), 30000);
}

export { mediaStatusIsConfigured };
